import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { useSaaSAuth } from '../contexts/SaaSAuthContext';
import DiscoveredContactsPanel, { ContactTypeBadge } from '../components/DiscoveredContactsPanel';
import { featureGate, formatRemaining, quotaGate } from '../lib/entitlements';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Globe, Phone, MapPin, Tag, Check, X as XIcon, Plus, ArrowsClockwise, EnvelopeSimple, ShieldCheck, MagnifyingGlass, SpinnerGap } from '@phosphor-icons/react';

const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  needs_review: 'bg-amber-50 text-amber-700 border-amber-200',
  exported: 'bg-purple-50 text-purple-700 border-purple-200',
  pushed_to_campaign: 'bg-green-50 text-green-800 border-green-300',
};

export default function CompanyDetailPage() {
  const { i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSaaSAuth();
  const discoveryPanelRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ full_name: '', email: '', job_title: '', salutation: '', company_id: id });
  const [discoveryBusy, setDiscoveryBusy] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';
  const pcdGate = featureGate(user, 'has_personal_contact_discovery');
  const revealGate = quotaGate(user, 'contacts_revealed', 'max_contact_reveals');
  const mxGate = featureGate(user, 'has_bulk_verification');
  const findContactsBlocked = !pcdGate.allowed || !revealGate.allowed;
  const findContactsLabel = !pcdGate.allowed
    ? pcdGate.label
    : (!revealGate.allowed ? revealGate.label : '');

  const fetchCompany = useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${id}`, { params: { locale: (i18n.resolvedLanguage || i18n.language || 'de').split('-')[0] } });
      setCompany(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);

  const updateStatus = async (status) => {
    try {
      await api.put(`/companies/${id}`, { status });
      toast.success(`Status updated to ${status}`);
      fetchCompany();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const recalculateScore = async () => {
    try {
      const { data } = await api.post(`/scoring/calculate/${id}`);
      toast.success(`Score: ${data.overall_score}`);
      fetchCompany();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addContact = async () => {
    try {
      await api.post('/contacts', { ...newContact, company_id: id });
      toast.success('Contact added. Score and readiness updated.');
      setShowAddContact(false);
      setNewContact({ full_name: '', email: '', job_title: '', salutation: '', company_id: id });
      fetchCompany();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const generateEmails = async () => {
    try {
      const { data } = await api.post(`/discovery/generate-emails/${id}`);
      if (data.generated > 0) {
        toast.success(`Generated ${data.generated} emails for ${data.domain}. Score recalculated.`);
      } else {
        toast.info('No new emails to generate — all common addresses already exist.');
      }
      fetchCompany();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const verifyMX = async () => {
    try {
      const { data } = await api.post(`/discovery/verify-mx/${id}`);
      toast.success(`MX verified: ${data.mx_confirmed} confirmed, ${data.invalid} invalid, ${data.risky} risky`);
      fetchCompany();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const runFindCompanyContacts = async () => {
    setDiscoveryBusy(true);
    try {
      await discoveryPanelRef.current?.discoverContacts?.();
    } finally {
      setDiscoveryBusy(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;
  if (!company) return <div className="text-red-500">Company not found</div>;

  const score = company.score_details;

  return (
    <div data-testid="company-detail-page" className="min-w-0 max-w-full">
      {/* Back */}
      <button onClick={() => navigate('/app/companies')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-4 transition-colors" data-testid="back-btn">
        <ArrowLeft size={16} /> Back to Companies
      </button>

      {/* Header */}
      <div className="border border-gray-200 rounded-md bg-white p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">{company.company_name}</h1>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium border ${STATUS_COLORS[company.status] || STATUS_COLORS.new}`}>
                {company.status?.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              {company.normalized_domain && (
                <span className="flex items-center gap-1"><Globe size={14} /> {company.normalized_domain}</span>
              )}
              {company.phone && (
                <span className="flex items-center gap-1"><Phone size={14} /> {company.phone}</span>
              )}
              {(company.city || company.region) && (
                <span className="flex items-center gap-1"><MapPin size={14} /> {[company.city, company.region, company.country].filter(Boolean).join(', ')}</span>
              )}
            </div>
            {company.tags && company.tags.length > 0 && (
              <div className="flex gap-1.5 mt-3">
                {company.tags.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono bg-gray-100 text-gray-700 border border-gray-200">
                    <Tag size={10} /> {t}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => updateStatus('approved')} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="approve-btn">
              <Check size={14} className="mr-1" /> Approve
            </Button>
            <Button variant="outline" size="sm" onClick={() => updateStatus('rejected')} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="reject-btn">
              <XIcon size={14} className="mr-1" /> Reject
            </Button>
            <Button variant="outline" size="sm" onClick={recalculateScore} data-testid="recalculate-score-btn">
              <ArrowsClockwise size={14} className="mr-1" /> Re-score
            </Button>
            <Button variant="outline" size="sm" onClick={generateEmails} data-testid="generate-emails-btn" title="Generate info@, sales@, contact@, office@ emails from domain">
              <EnvelopeSimple size={14} className="mr-1" /> Gen Emails
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={verifyMX}
              disabled={!mxGate.allowed}
              data-testid="verify-mx-btn"
              title={mxGate.allowed ? 'Check MX records for all contact email domains' : mxGate.label}
            >
              <ShieldCheck size={14} className="mr-1" />
              {mxGate.allowed ? 'Verify MX' : mxGate.label}
            </Button>
          </div>
        </div>

        {/* Score banner */}
        {score && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-xs font-mono uppercase tracking-wider text-gray-500">Overall</p>
                <p className="text-3xl font-mono font-bold text-gray-900">{score.overall_score}</p>
              </div>
              <div className="flex-1 grid grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { l: 'Industry', v: score.industry_score },
                  { l: 'Website', v: score.website_quality_score },
                  { l: 'Location', v: score.location_score },
                  { l: 'Contacts', v: score.contact_quality_score },
                  { l: 'Email', v: score.email_quality_score },
                  { l: 'Custom', v: score.custom_rule_score },
                ].map(s => (
                  <div key={s.l} className="text-center">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-gray-400">{s.l}</p>
                    <p className="text-sm font-mono font-bold text-gray-700">{s.v || 0}</p>
                  </div>
                ))}
              </div>
            </div>
            {score.reasoning && score.reasoning.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {score.reasoning.map((r, i) => (
                  <span key={i} className="text-xs font-mono bg-gray-50 text-gray-600 px-2 py-0.5 rounded border border-gray-100">{r}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="contacts" className="w-full">
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="contacts">Contacts ({company.contacts?.length || 0})</TabsTrigger>
          <TabsTrigger value="info">Details</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="contacts" className="mt-4 min-w-0 max-w-full">
          <div className="mb-4 rounded-md border border-gray-200 bg-white p-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            {isSuperAdmin && (
              <p
                className="text-xs font-mono text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1"
                data-testid="company-contacts-tab-pcd-debug"
              >
                Personal Contact Discovery UI loaded
              </p>
            )}
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full sm:w-auto sm:ml-auto">
              <Button
                size="sm"
                onClick={runFindCompanyContacts}
                disabled={discoveryBusy || !company.normalized_domain || findContactsBlocked}
                className="bg-black text-white hover:bg-gray-800 w-full sm:w-auto"
                data-testid="find-company-contacts-tab-btn"
                title={findContactsBlocked ? findContactsLabel : undefined}
              >
                {discoveryBusy ? (
                  <>
                    <SpinnerGap size={14} className="mr-1 animate-spin" /> Finding company contacts...
                  </>
                ) : findContactsBlocked ? (
                  findContactsLabel
                ) : (
                  <>
                    <MagnifyingGlass size={14} className="mr-1" /> Find Company Contacts
                  </>
                )}
              </Button>
              {!findContactsBlocked && revealGate.remaining !== Number.POSITIVE_INFINITY && (
                <span className="text-xs text-gray-500 self-center font-mono" data-testid="reveals-remaining">
                  {formatRemaining(revealGate.remaining)} reveals left
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowAddContact(true)}
                className="w-full sm:w-auto"
                data-testid="add-contact-btn"
              >
                <Plus size={14} className="mr-1" /> Add Contact
              </Button>
            </div>
          </div>

          <DiscoveredContactsPanel
            ref={discoveryPanelRef}
            companyId={id}
            companyContacts={company.contacts || []}
            hasDomain={Boolean(company.normalized_domain)}
            isSuperAdmin={isSuperAdmin}
            showFindButton={false}
            onImported={fetchCompany}
          />

          <div className="flex items-center justify-between mb-3 mt-2">
            <h3 className="text-sm font-medium text-gray-900">Saved Contacts</h3>
          </div>
          <div className="border border-gray-200 rounded-md bg-white overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Name</th>
                  <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Email</th>
                  <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Salutation</th>
                  <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Title</th>
                  <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Type</th>
                  <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Status</th>
                  <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">Primary</th>
                </tr>
              </thead>
              <tbody>
                {(!company.contacts || company.contacts.length === 0) ? (
                  <tr><td colSpan="7" className="py-8 text-center text-gray-400 text-sm">No contacts yet</td></tr>
                ) : company.contacts.map((c, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900">{c.full_name || '-'}</td>
                    <td className="py-3 px-4 text-sm font-mono text-gray-700">{c.email}</td>
                    <td className="py-3 px-4 text-sm text-gray-700 min-w-[240px]">{c.salutation || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{c.job_title || '-'}</td>
                    <td className="py-3 px-4"><ContactTypeBadge contact={c} /></td>
                    <td className="py-3 px-4">
                      <span className="text-xs font-mono bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">{c.email_status || 'unverified'}</span>
                    </td>
                    <td className="py-3 px-4">{c.is_primary ? <Check size={16} className="text-emerald-600" /> : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="info" className="mt-4">
          <div className="border border-gray-200 rounded-md bg-white p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-mono uppercase tracking-wider text-gray-400">Industry</label><p className="text-sm text-gray-900 mt-1">{company.industry_raw || '-'}</p></div>
              <div><label className="text-xs font-mono uppercase tracking-wider text-gray-400">Size Estimate</label><p className="text-sm text-gray-900 mt-1">{company.employee_size_estimate || '-'}</p></div>
              <div><label className="text-xs font-mono uppercase tracking-wider text-gray-400">Website</label><p className="text-sm text-gray-900 mt-1 font-mono">{company.website_url || '-'}</p></div>
              <div><label className="text-xs font-mono uppercase tracking-wider text-gray-400">Domain</label><p className="text-sm text-gray-900 mt-1 font-mono">{company.normalized_domain || '-'}</p></div>
              <div><label className="text-xs font-mono uppercase tracking-wider text-gray-400">Address</label><p className="text-sm text-gray-900 mt-1">{company.address || '-'}</p></div>
              <div><label className="text-xs font-mono uppercase tracking-wider text-gray-400">LinkedIn</label><p className="text-sm text-gray-900 mt-1 font-mono">{company.linkedin_url || '-'}</p></div>
            </div>
            {company.company_description && (
              <div><label className="text-xs font-mono uppercase tracking-wider text-gray-400">Description</label><p className="text-sm text-gray-700 mt-1">{company.company_description}</p></div>
            )}
            {company.notes && (
              <div><label className="text-xs font-mono uppercase tracking-wider text-gray-400">Notes</label><p className="text-sm text-gray-700 mt-1">{company.notes}</p></div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
            {(!company.activity || company.activity.length === 0) ? (
              <p className="py-8 text-center text-gray-400 text-sm">No activity yet</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {company.activity.map((a, i) => (
                  <div key={i} className="px-4 py-3 flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-gray-300 shrink-0" />
                    <span className="text-sm text-gray-700 flex-1">{a.action}: {a.details}</span>
                    <span className="text-xs font-mono text-gray-400">{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Contact Dialog */}
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Add Contact</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Full Name</label>
              <Input value={newContact.full_name} onChange={e => setNewContact(p => ({...p, full_name: e.target.value}))} data-testid="contact-name-input" /></div>
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Email *</label>
              <Input type="email" value={newContact.email} onChange={e => setNewContact(p => ({...p, email: e.target.value}))} data-testid="contact-email-input" /></div>
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Job Title</label>
              <Input value={newContact.job_title} onChange={e => setNewContact(p => ({...p, job_title: e.target.value}))} /></div>
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Salutation override (optional)</label>
              <Input value={newContact.salutation} onChange={e => setNewContact(p => ({...p, salutation: e.target.value}))} placeholder="Leave blank for automatic salutation" /></div>
            <div className="flex justify-end gap-3"><Button variant="outline" onClick={() => setShowAddContact(false)}>Cancel</Button>
              <Button onClick={addContact} className="bg-black text-white hover:bg-gray-800" disabled={!newContact.email} data-testid="save-contact-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
